import React from 'react';
import { X, MessageCircleQuestion, Volume2 } from 'lucide-react';
import { DemoWaveform } from './DemoWaveform';
import { DemoDecision } from './DemoDecision';
import type { DemoDecisionConfig, DemoPhase } from '../../types/demo';

interface DemoBubbleProps {
  /** Screen position of the cursor (bubble offsets from this) */
  cursorX: number;
  cursorY: number;
  visible: boolean;
  phase: DemoPhase;
  /** Subtitle text to display */
  subtitle: string;
  /** Whether audio is currently playing */
  audioPlaying: boolean;
  /** Active decision (null if not in decision mode) */
  decision: DemoDecisionConfig | null;
  /** Callback when a decision option is selected */
  onDecisionSelect: (nextStepId: string) => void;
  /** Callback when user wants to ask a question */
  onAskQuestion: () => void;
  /** Callback to dismiss the demo */
  onDismiss: () => void;
  /** Callback to pause/resume */
  onTogglePause: () => void;
  paused: boolean;
}

/**
 * Floating companion bubble that follows the animated cursor.
 * Three modes: speaking (dots + subtitle), decision (choices), complete.
 * Contracts to a minimal pill when idle (no subtitle, no decision).
 */
export const DemoBubble: React.FC<DemoBubbleProps> = ({
  cursorX,
  cursorY,
  visible,
  phase,
  subtitle,
  audioPlaying,
  decision,
  onDecisionSelect,
  onAskQuestion,
  onDismiss,
  onTogglePause,
  paused,
}) => {
  const isDecisionMode = phase === 'decision' && decision;
  const isComplete = phase === 'complete';
  const hasContent = !!(subtitle || isDecisionMode || isComplete);

  // Expanded bubble width vs compact pill width — smaller on mobile
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 720;
  const isMobile = viewportW < 640;
  const bubbleWidth = hasContent ? (isMobile ? 240 : 280) : (isMobile ? 100 : 120);
  const bubbleOffset = { x: isMobile ? 16 : 28, y: isMobile ? 16 : 20 };

  let left: number;
  let top: number;

  if (isMobile && hasContent) {
    // On mobile with content: anchor at center-bottom for stable reading
    left = (viewportW - bubbleWidth) / 2;
    top = viewportH - 280;
  } else {
    left = cursorX + bubbleOffset.x;
    top = cursorY + bubbleOffset.y;

    // Clamp to viewport
    if (left + bubbleWidth > viewportW - 16) left = cursorX - bubbleWidth - 12;
    if (top + 200 > viewportH - 16) top = cursorY - 200;
    if (left < 16) left = 16;
    if (top < 16) top = 16;
  }

  return (
    <div
      className="fixed pointer-events-auto"
      style={{
        zIndex: 9999,
        left,
        top,
        width: bubbleWidth,
        opacity: visible ? 1 : 0,
        transform: visible ? 'scale(1)' : 'scale(0.9)',
        transition: 'opacity 0.4s ease-out, transform 0.4s ease-out, width 0.4s ease-out, left 0.3s ease-out, top 0.3s ease-out',
      }}
    >
      <div
        className="rounded-xl bg-gray-900/95 backdrop-blur-sm border border-white/10 shadow-2xl overflow-hidden"
        style={{
          transition: 'all 0.4s ease-out',
        }}
      >
        {/* Header bar — always visible */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
          <Volume2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
          <DemoWaveform playing={audioPlaying && !paused} />
          <div className="flex-1" />
          {hasContent && (
            <button
              onClick={onAskQuestion}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              title="Ask a question"
            >
              <MessageCircleQuestion className="h-3.5 w-3.5 text-white/60 hover:text-white" />
            </button>
          )}
          <button
            onClick={onDismiss}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Exit demo"
          >
            <X className="h-3.5 w-3.5 text-white/60 hover:text-white" />
          </button>
        </div>

        {/* Content area — expands/contracts with CSS transition */}
        <div
          style={{
            maxHeight: hasContent ? 300 : 0,
            opacity: hasContent ? 1 : 0,
            overflow: 'hidden',
            transition: 'max-height 0.4s ease-out, opacity 0.3s ease-out, padding 0.4s ease-out',
            padding: hasContent ? '10px 12px' : '0 12px',
          }}
        >
          {isDecisionMode && decision ? (
            <DemoDecision decision={decision} onSelect={onDecisionSelect} />
          ) : isComplete ? (
            <div className="space-y-2">
              <p className="text-sm text-white/90">Demo complete! What would you like to do?</p>
              <div className="flex flex-col gap-2 sm:gap-1.5">
                <button
                  onClick={() => onDecisionSelect('__replay')}
                  className="px-4 sm:px-3 py-3 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 transition-colors"
                >
                  Replay demo
                </button>
                <button
                  onClick={onAskQuestion}
                  className="px-4 sm:px-3 py-3 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-colors"
                >
                  Ask a question
                </button>
                <button
                  onClick={onDismiss}
                  className="px-4 sm:px-3 py-3 sm:py-1.5 rounded-full text-sm sm:text-xs font-medium bg-white/10 hover:bg-white/20 text-white/70 border border-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          ) : subtitle ? (
            <p className="text-sm text-white/90 leading-relaxed">{subtitle}</p>
          ) : null}
        </div>

        {/* Footer with controls — only when expanded with content and not in decision/complete */}
        {hasContent && !isComplete && !isDecisionMode && (
          <div className="flex items-center gap-2 px-3 py-1.5 border-t border-white/5">
            <button
              onClick={onTogglePause}
              className="text-[10px] text-white/40 hover:text-white/70 transition-colors"
            >
              {paused ? '▶ Resume' : '⏸ Pause'}
            </button>
            <span className="text-[10px] text-white/20 ml-auto">ESC to exit</span>
          </div>
        )}
      </div>
    </div>
  );
};
